import { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import { get_embedded_sale_context } from '../utils/embeddedSale';

const INTERCOM_APP_ID = 'wwj0mgwl';

type IntercomSettings = {
	app_id: string;
	email: string;
	name: string;
	vertical_padding: number;
	hide_default_launcher: true;
	language_override: 'es';
	country: string;
	master_store_email: string;
	store_role: string;
	source: 'portal';
	channel: 'portal';
};

type IntercomArgument = Partial<IntercomSettings> | (() => void);
type IntercomCall = [command: string, argument?: IntercomArgument];
type IntercomFunction = ((...args: IntercomCall) => void) & { q?: IntercomCall[] };

declare global {
	interface Window {
		Intercom?: IntercomFunction;
		intercomSettings?: IntercomSettings;
	}
}

function loadIntercom() {
	if (window.Intercom) return;

	const intercom = ((...args: IntercomCall) => intercom.q?.push(args)) as IntercomFunction;
	intercom.q = [];
	window.Intercom = intercom;

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
	script.onerror = () => {
		script.remove();
		if (window.Intercom === intercom) delete window.Intercom;
	};
	document.head.appendChild(script);
}

export function useIntercom() {
	const { authenticated, userData, accessToOtherStores, CountrySettings } = useContext(GlobalContext);
	const [messengerOpen, setMessengerOpen] = useState(false);
	const email = userData?.email;
	const country = userData?.country;
	const accessType = userData?.access_type;
	const storeName = userData?.store_name;
	const masterStoreEmail = userData?.master_store?.id;
	const intercomEnabled = CountrySettings?.feature_flags?.intercom_portal === true;
	const isImpersonating = accessToOtherStores?.isImpersonating === true;
	const storeRole = userData?.is_seller ? 'seller' : userData?.master_user ? 'master' : 'branch';
	const intercomAvailable =
		authenticated && !!email && !!country && !!masterStoreEmail && intercomEnabled && !accessType && !isImpersonating && !get_embedded_sale_context();

	useEffect(() => {
		if (!intercomAvailable) {
			setMessengerOpen(false);
			window.Intercom?.('shutdown');
			return;
		}

		loadIntercom();
		const desktopQuery = window.matchMedia('(min-width: 768px)');
		const settings: IntercomSettings = {
			app_id: INTERCOM_APP_ID,
			email,
			name: storeName || email,
			vertical_padding: desktopQuery.matches ? 20 : 88,
			hide_default_launcher: true,
			language_override: 'es',
			country,
			master_store_email: masterStoreEmail,
			store_role: storeRole,
			source: 'portal',
			channel: 'portal'
		};
		window.intercomSettings = settings;
		window.Intercom?.('boot', settings);
		window.Intercom?.('onShow', () => setMessengerOpen(true));
		window.Intercom?.('onHide', () => setMessengerOpen(false));

		const updateVerticalPadding = ({ matches }: MediaQueryListEvent) => {
			settings.vertical_padding = matches ? 20 : 88;
			window.Intercom?.('update', { vertical_padding: settings.vertical_padding });
		};
		desktopQuery.addEventListener('change', updateVerticalPadding);

		return () => {
			desktopQuery.removeEventListener('change', updateVerticalPadding);
			setMessengerOpen(false);
			window.Intercom?.('shutdown');
			delete window.intercomSettings;
		};
	}, [email, country, storeName, masterStoreEmail, storeRole, intercomAvailable]);

	return {
		intercomOpen: messengerOpen,
		intercomAvailable,
		openIntercom: () => window.Intercom?.('show')
	};
}
